import {
  Breadcrumb,
  Layout,
  Pagination,
  Popover,
  Select,
  Table,
  Button,
  Input,
  Space,
} from "antd";
import { SearchOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";

import { ColumnType } from "antd/es/table/interface";
import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { connect } from "react-redux";
import {
  fastMovingSlowMovingItems,
  getCategoryWiseItems,
  getStoreWiseItems,
  onTimeDelayProduction,
  stockAgeing,
  stockLevelAnalysis,
  workOrderStatus,
} from "../../../redux/actions/API/dashboardActions";
import Loader from "../../../Home/Loader/Loader";

interface IStoreWiseItemsProps {
  companyId: number;
  storeWiseItemsData: any;
  storeWiseItemsLoading: boolean;
  getStoreWiseItems: Function;
  uomData: any;
  getStoresData: any;
}

interface StoreWiseItemData {
  key: string;
  itemId: string;
  itemName: string;
  currentStock: number;
  minStock: number | null;
  maxStock: number | null;
  stockValue: number;
  metricsUnit: number | null;
  itemDetails: any;
  [key: string]: any;
}

type SearchableColumnKey = "itemId" | "itemName" | "uom" | "stockValue";

const StoreWiseItems: React.FC<IStoreWiseItemsProps> = ({
  companyId,
  storeWiseItemsData,
  storeWiseItemsLoading,
  getStoreWiseItems,
  uomData,
  getStoresData,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [columnSearchText, setColumnSearchText] = useState<
    Record<SearchableColumnKey, string>
  >({
    itemId: "",
    itemName: "",
    uom: "",
    stockValue: "",
  });

  const storeId = searchParams.get("storeId");

  // Fetch data on component mount and when dependencies change
  useEffect(() => {
    if (companyId && storeId) {
      getStoreWiseItems({
        companyId: Number(companyId),
        storeId: Number(storeId),
      });
    }
  }, [companyId, storeId, getStoreWiseItems]);

  const formatCurrency = (value: number) => {
    const formatted = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(value || 0);
    return formatted.replace(/^(\D+)(.*)$/, "$1 $2");
  };

  const getUomCode = (uomId: number | null) => {
    if (!uomId || !Array.isArray(uomData)) return "";
    return uomData.find((u: any) => u.id === uomId)?.code || "";
  };

  const uomCodeMap = useMemo(() => {
    if (!Array.isArray(uomData)) return {};

    return uomData.reduce((acc: Record<number, string>, curr: any) => {
      if (typeof curr?.id === "number") {
        acc[curr.id] = curr?.code || "";
      }
      return acc;
    }, {});
  }, [uomData]);

  const getColumnValueForSearch = (
    record: StoreWiseItemData,
    columnKey: SearchableColumnKey,
  ): string => {
    if (columnKey === "itemId")
      return String(record.itemId || "").toLowerCase();
    if (columnKey === "itemName")
      return String(record.itemName || "").toLowerCase();
    if (columnKey === "uom")
      return String(uomCodeMap[record.metricsUnit ?? 0] || "").toLowerCase();
    return String(record.stockValue ?? "").toLowerCase();
  };

  // Transform API data to table format
  const rawItems = Array.isArray(storeWiseItemsData) ? storeWiseItemsData : [];

  const tableData: StoreWiseItemData[] = rawItems.map(
    (item: any, index: number) => {
      const details = item?.itemId || {};
      const quantity = Number(item?.quantity) || 0;
      const avgPrice = Number(item?.averagePrice);
      const fallbackPrice = Number(details?.price) || 0;

      return {
        ...item,
        key: item.id || index,
        itemId: String(details?.itemId || ""),
        itemName: String(details?.itemName || ""),
        currentStock: quantity,
        minStock: details?.minStock ?? null,
        maxStock: details?.maxStock ?? null,
        stockValue: Number.isFinite(avgPrice) ? avgPrice : fallbackPrice,
        metricsUnit: details?.metricsUnit ?? null,
        itemDetails: details,
      };
    },
  );

  const fullyFilteredData = useMemo(() => {
    return tableData.filter((record) => {
      const searchableColumns = Object.keys(
        columnSearchText,
      ) as SearchableColumnKey[];

      return searchableColumns.every((columnKey) => {
        const searchText = columnSearchText[columnKey].trim().toLowerCase();
        if (!searchText) return true;
        return getColumnValueForSearch(record, columnKey).includes(searchText);
      });
    });
  }, [tableData, columnSearchText, uomCodeMap]);

  // Calculate total stock value using useMemo
  const totalStockValue = useMemo(() => {
    return fullyFilteredData.reduce(
      (sum, item) => sum + (Number(item.stockValue) || 0),
      0,
    );
  }, [fullyFilteredData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [tableData.length, columnSearchText]);

  // Handle store selection change
  const handleStoreChange = (value: number) => {
    navigate(`/storeWiseItems?storeId=${value}`);
  };

  const getColumnSearchProps = (
    columnKey: SearchableColumnKey,
    label: string,
  ): Partial<ColumnType<StoreWiseItemData>> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${label}`}
          value={(selectedKeys[0] as string) || ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => {
            const value = String(selectedKeys[0] || "").trim();
            setColumnSearchText((prev) => ({ ...prev, [columnKey]: value }));
            confirm();
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              const value = String(selectedKeys[0] || "").trim();
              setColumnSearchText((prev) => ({ ...prev, [columnKey]: value }));
              confirm();
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters?.();
              setSelectedKeys([]);
              setColumnSearchText((prev) => ({ ...prev, [columnKey]: "" }));
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    filteredValue: columnSearchText[columnKey]
      ? [columnSearchText[columnKey]]
      : null,
    onFilter: () => true,
  });

  // Define table columns
  const columns: ColumnType<StoreWiseItemData>[] = [
    {
      title: "Item ID",
      dataIndex: "itemId",
      key: "itemId",
      width: 150,
      fixed: "left",
      sorter: (a: any, b: any) =>
        String(a.itemId || "").localeCompare(
          String(b.itemId || ""),
          undefined,
          {
            numeric: true,
            sensitivity: "base",
          },
        ),
      render: (value: string, record: StoreWiseItemData) => value,
      ...getColumnSearchProps("itemId", "Item ID"),
    },
    {
      title: "Item Name",
      dataIndex: "itemName",
      key: "itemName",
      width: 260,
      sorter: (a: any, b: any) =>
        (a.itemName || "").localeCompare(b.itemName || ""),
      render: (value: string) => (
        <span style={{ fontWeight: 500 }}>{value || "N/A"}</span>
      ),
      ...getColumnSearchProps("itemName", "Item Name"),
    },
    {
      title: "Current Stock",
      dataIndex: "currentStock",
      key: "currentStock",
      width: 180,
      sorter: (a: any, b: any) =>
        (Number(a.currentStock) || 0) - (Number(b.currentStock) || 0),
      render: (value: number, record: StoreWiseItemData) => (
        <span>
          {Number(value || 0).toFixed(2)} {getUomCode(record.metricsUnit)}
        </span>
      ),
    },
    {
      title: "UOM",
      dataIndex: "metricsUnit",
      key: "uom",
      width: 180,
      sorter: (a: any, b: any) =>
        String(uomCodeMap[a.metricsUnit ?? 0] || "").localeCompare(
          String(uomCodeMap[b.metricsUnit ?? 0] || ""),
        ),
      render: (value: number) => <span>{uomCodeMap[value ?? 0] || "N/A"}</span>,
      ...getColumnSearchProps("uom", "UOM"),
    },
    {
      title: "Min/Max Stock Data",
      dataIndex: "stockStock",
      key: "stockStock",
      width: 220,
      sorter: (a: any, b: any) =>
        (Number(a.currentStock) || 0) - (Number(b.currentStock) || 0),
      render: (_: any, record: StoreWiseItemData) => (
        <div>
          <div>Min Stock: {record.minStock ?? "N/A"}</div>
          <div>Max Stock: {record.maxStock ?? "N/A"}</div>
        </div>
      ),
    },
    {
      title: "Stock Value",
      dataIndex: "stockValue",
      key: "stockValue",
      width: 180,
      sorter: (a: any, b: any) =>
        (Number(a.stockValue) || 0) - (Number(b.stockValue) || 0),
      render: (value: number) => (
        <span>{formatCurrency(Number(value) || 0)}</span>
      ),
      ...getColumnSearchProps("stockValue", "Stock Value"),
    },
  ];

  const storeOptions = Array.isArray(getStoresData)
    ? getStoresData.map((store: any) => ({
        label: store.name,
        value: store.id,
      }))
    : [];

  const total = fullyFilteredData.length;
  const paginatedData = fullyFilteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const breadcrumbItems = [
    { key: "dashboard", title: <Link to="/dashboard">Dashboard</Link> },
    { key: "storeWiseItems", title: "Store Wise Items" },
  ];

  return (
    <>
      <Loader loading={storeWiseItemsLoading} />
      <Layout className="layout">
        <div className="flexBox mb-flexFlow" style={{ alignItems: "center" }}>
          <Breadcrumb className="breadcrumb" items={breadcrumbItems} />
          <Button
            type="primary"
            icon={<ArrowLeftOutlined style={{ color: "#ffffff" }} />}
            onClick={() => navigate("/dashboard")}
          >
            Back to Dashboard
          </Button>
        </div>
        <Content className="content-section">
          <div className="mb-10">
            <div className="flexBox mb-10">
              <Select
                style={{ width: 200 }}
                placeholder="Select a store"
                value={storeId ? Number(storeId) : undefined}
                onChange={handleStoreChange}
                options={storeOptions}
              />
              <div
                style={{
                  fontWeight: "500",
                  padding: "4px 11px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "8px",
                }}
              >
                Store value: ₹
                {totalStockValue.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
            {fullyFilteredData && (
              <>
                <Table
                  columns={columns}
                  rowKey="key"
                  dataSource={paginatedData}
                  bordered
                  pagination={false}
                  scroll={{ x: "100%", y: "calc(100vh - 320px)" }}
                />
                <Pagination
                  total={total}
                  defaultPageSize={20}
                  current={currentPage}
                  pageSize={pageSize}
                  onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                  }}
                  showSizeChanger
                  style={{ textAlign: "center", marginTop: 20 }}
                />
              </>
            )}
          </div>
        </Content>
      </Layout>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  companyId: state.api.login?.data?.companyId,
  storeWiseItemsData: state.api.getStoreWiseItems?.storeWiseItems?.storeItems,
  storeWiseItemsLoading: state.api.getStoreWiseItems?.loading,
  uomData: state.api.getUOM.data,
  getStoresData: state.api.getStore.data,
});

const mapDispatchToProps = (dispatch: any) => ({
  getStoreWiseItems: (payload: any) => dispatch(getStoreWiseItems(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StoreWiseItems);
